import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, AlertCircle, Zap, ArrowDownCircle, Loader, Target, Copy, ExternalLink, Shuffle, ChevronUp, Settings, Search, Check, RefreshCcw, X } from 'lucide-react';
import { Token } from '../../types/supabase';
import { useWalletUser } from '../../hooks/useWalletUser';
import { tradeEngine } from '../../services/tradeEngine';
import { getUserProfile } from '../../services/userProfileService';
import { getBalance, getTokenBalances } from '../../services/shyftService';
import { getTokenBalance } from '../../services/solanatrackerService';
import { SOL_MINT } from '../../services/jupiterService';
import TokenSearchModal from '../../components/TokenSearchModal';
import { userService } from '../../services/userService';
import { injectGlobalStyles, widgetClassName, ekkoColors } from '../../styles/globalWidgetStyles';

// Add localStorage keys for slippage preferences
const SLIPPAGE_STORAGE_KEY = 'ekko_trader_slippage';

// Additional custom styles for the TradesBuySell widget
const customStyles = `
  .trades-buy-sell .token-select-button {
    background-color: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    transition: border-color 0.2s, background-color 0.2s;
  }
  
  .trades-buy-sell .token-select-button:hover {
    border-color: #24c35b;
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  .trades-buy-sell .amount-button {
    background-color: white;
    border: 1px solid #d1d5db;
    color: #374151;
    border-radius: 0.25rem;
    transition: border-color 0.2s, background-color 0.2s;
  }
  
  .trades-buy-sell .amount-button:hover {
    border-color: #24c35b;
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  .trades-buy-sell .amount-button.active {
    background-color: #24c35b;
    border-color: #24c35b;
    color: white;
  }
  
  .trades-buy-sell .slippage-button {
    background-color: white;
    border: 1px solid #d1d5db;
    color: #374151;
    border-radius: 0.25rem;
    transition: border-color 0.2s, background-color 0.2s;
  }
  
  .trades-buy-sell .slippage-button:hover {
    border-color: #24c35b;
    background-color: rgba(36, 195, 91, 0.05);
  }
  
  .trades-buy-sell .slippage-button.active {
    background-color: #24c35b;
    border-color: #24c35b;
    color: white;
  }
  
  .trades-buy-sell .mode-toggle {
    background-color: #f9fafb;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    overflow: hidden;
  }
  
  .trades-buy-sell .mode-toggle button {
    transition: background-color 0.2s, color 0.2s;
  }
  
  .trades-buy-sell .mode-toggle button.active-buy {
    background-color: #24c35b;
    color: white;
  }
  
  .trades-buy-sell .mode-toggle button.active-sell {
    background-color: #ef4444;
    color: white;
  }
  
  .trades-buy-sell .notification {
    border-radius: 0.375rem;
    overflow: hidden;
  }
  
  .trades-buy-sell .notification.success {
    background-color: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
  }
  
  .trades-buy-sell .notification.error {
    background-color: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
  }
`;

// This component will be used both as a standalone modal and as a canvas widget content
interface TradesBuySellProps {
  selectedToken?: Token;
  initialMode?: 'buy' | 'sell';
  onClose?: () => void;
  isWidget?: boolean; // Flag to indicate if this is being rendered in a canvas widget
}

const TradesBuySell: React.FC<TradesBuySellProps> = ({ 
  selectedToken, 
  initialMode = 'buy',
  onClose, 
  isWidget = false 
}) => {
  const walletUser = useWalletUser();
  
  // Token state
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  const [isTokenSearchModalOpen, setIsTokenSearchModalOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  
  // Trading state
  const [amount, setAmount] = useState<string>(initialMode === 'buy' ? '0.1' : '1');
  const [slippage, setSlippage] = useState<string>('20');
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>(initialMode);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [txSignature, setTxSignature] = useState<string | null>(null);
  const [activeTradingWallet, setActiveTradingWallet] = useState<string | null>(null);
  
  // Balances
  const [solBalance, setSolBalance] = useState<string>('0');
  const [tokenBalance, setTokenBalance] = useState<string>('0');
  const [isLoadingBalances, setIsLoadingBalances] = useState<boolean>(false);

  // Token information and price estimation
  const [estimatedOutput, setEstimatedOutput] = useState<string | null>(null);
  const [isGettingEstimate, setIsGettingEstimate] = useState<boolean>(false);
  
  // Add this state for transaction lockout
  const [isBalancePending, setIsBalancePending] = useState<boolean>(false);
  
  // Add state for custom slippage input
  const [showCustomSlippage, setShowCustomSlippage] = useState<boolean>(false);
  const [customSlippageInput, setCustomSlippageInput] = useState<string>('');
  const [slippageMenuOpen, setSlippageMenuOpen] = useState<boolean>(false);
  
  // Add state for notification timeouts
  const [notificationTimeout, setNotificationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Use the effective token (either local state or prop)
  const effectiveToken = localSelectedToken || selectedToken;
  
  // Update local state when prop changes
  useEffect(() => {
    console.log('🔄 TradesBuySell: selectedToken prop changed:', selectedToken);
    if (selectedToken) {
      console.log(`🔄 TradesBuySell: Setting local token to ${selectedToken.symbol} (${selectedToken.mint})`);
      setLocalSelectedToken(selectedToken);
    }
  }, [selectedToken]);

  // Get active trading wallet when component loads - SEPARATE from token logic
  useEffect(() => {
    const fetchActiveTradingWallet = async () => {
      if (walletUser.walletAddress) {
        try {
          setIsLoadingBalances(true);
          const profile = await getUserProfile(walletUser.walletAddress);
          
          if (profile?.active_trade_wallet) {
            setActiveTradingWallet(profile.active_trade_wallet);
            console.log('Active trading wallet found:', profile.active_trade_wallet);
            
            // Fetch SOL balance
            const balance = await getBalance(profile.active_trade_wallet);
            setSolBalance((balance / 1_000_000_000).toFixed(5)); // Convert lamports to SOL
            
          } else {
            console.log('No active trading wallet found for user');
            setError('Please activate a trading wallet to use the Trade feature');
          }
        } catch (err) {
          console.error('Error fetching active trading wallet:', err);
          setError('Error connecting to trading wallet');
        } finally {
          setIsLoadingBalances(false);
        }
      }
    };

    fetchActiveTradingWallet();
    
    // Listen for active wallet changes
    const handleActiveWalletChanged = (event: CustomEvent<{previousWallet: string | null, currentWallet: string | null}>) => {
      const { currentWallet } = event.detail;
      console.log('Active trading wallet changed event received:', currentWallet);
      
      // Update the active trading wallet
      setActiveTradingWallet(currentWallet);
      
      // If wallet was deactivated, show error message
      if (!currentWallet) {
        setError('Please activate a trading wallet to use the Trade feature');
        setSolBalance('0'); // Clear SOL balance
      } else {
        // Clear error if a new wallet was activated
        if (error === 'Please activate a trading wallet to use the Trade feature' || 
            error === 'Please activate a trading wallet in your wallet dashboard first') {
          setError(null);
        }
        
        // Refresh SOL balance for new wallet
        refreshSolBalance(currentWallet);
        
        // Refresh token balance if we have a token selected
        if (effectiveToken?.mint) {
          refreshTokenBalance(currentWallet, effectiveToken.mint);
        }
      }
    };
    
    // Add event listener
    window.addEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('ekko:active_wallet_changed', handleActiveWalletChanged as EventListener);
    };
  }, [walletUser.walletAddress]); // 🔥 REMOVED effectiveToken?.mint and error dependencies

  // Separate effect for token balance fetching when token changes
  useEffect(() => {
    if (activeTradingWallet && effectiveToken?.mint) {
      console.log(`🔄 Token changed to ${effectiveToken.symbol}, fetching token balance for wallet: ${activeTradingWallet.slice(0, 8)}...`);
      refreshTokenBalance(activeTradingWallet, effectiveToken.mint);
    } else if (effectiveToken?.mint && !activeTradingWallet) {
      console.log('⚠️ Token selected but no active trading wallet available');
      setTokenBalance('0');
    }
  }, [effectiveToken?.mint, activeTradingWallet]);

  // Function to refresh SOL balance only
  const refreshSolBalance = useCallback(async (walletAddress: string) => {
    try {
      console.log(`🔄 Refreshing SOL balance for wallet: ${walletAddress.slice(0, 8)}...`);
      const balance = await getBalance(walletAddress);
      setSolBalance((balance / 1_000_000_000).toFixed(5));
      console.log(`✅ SOL balance updated: ${(balance / 1_000_000_000).toFixed(5)}`);
    } catch (err) {
      console.error('Error refreshing SOL balance:', err);
    }
  }, []);

  // Function to refresh token balance only
  const refreshTokenBalance = useCallback(async (walletAddress: string, tokenMint: string) => {
    try {
      setIsLoadingBalances(true);
      console.log(`🔄 Refreshing token balance for ${tokenMint.slice(0, 8)}... on wallet: ${walletAddress.slice(0, 8)}...`);
      
      // First try direct API call for most up-to-date balance
      const tokBalance = await getTokenBalance(walletAddress, tokenMint, true);
      console.log(`Token balance from direct API: ${tokBalance}`);
      
      // If we got a valid balance, use it
      if (tokBalance) {
        setTokenBalance(tokBalance);
        console.log(`✅ Token balance updated: ${tokBalance}`);
        return;
      }
      
      // If direct API call didn't return a valid balance, try alternative data sources
      console.log("Direct API balance invalid, trying alternative data sources...");
      
      // Import walletBalanceService store dynamically to avoid circular dependencies
      const { useWalletBalances } = await import('../../services/walletBalanceService');
      const walletBalanceState = useWalletBalances.getState();
      
      // Check if the token balance exists in the wallet state
      const tokenData = walletBalanceState.tokenBalances.find(
        tb => tb.mint === tokenMint
      );
      
      if (tokenData && tokenData.balance) {
        console.log(`Token balance from global state: ${tokenData.balance}`);
        setTokenBalance(tokenData.balance);
        return;
      }
      
      // If no balance found so far, force a refresh via solanatrackerService
      console.log("No balance found in cached sources, attempting direct refresh...");
      try {
        const { refreshWalletTokens } = await import('../../services/solanatrackerService');
        const refreshResult = await refreshWalletTokens(walletAddress, tokenMint);
        
        if (refreshResult.status === 'success' && refreshResult.data?.tokens?.length > 0) {
          const refreshedToken = refreshResult.data.tokens[0];
          console.log(`Refreshed token balance via direct refresh: ${refreshedToken.balance}`);
          setTokenBalance(refreshedToken.balance);
          return;
        }
      } catch (refreshErr) {
        console.error("Error during direct token refresh:", refreshErr);
      }
      
      // Force a check of all token balances as a last resort
      console.log(`No cached token balance found, fetching all token balances...`);
      const allBalances = await getTokenBalances(walletAddress);
      
      if (allBalances && allBalances[tokenMint]) {
        console.log(`Found token balance in all balances: ${allBalances[tokenMint]}`);
        setTokenBalance(allBalances[tokenMint]);
      } else {
        console.log(`Still no token balance found, setting to 0`);
        setTokenBalance('0');
      }
    } catch (err) {
      console.error('Error refreshing token balance:', err);
      setTokenBalance('0');
    } finally {
      setIsLoadingBalances(false);
    }
  }, []);

  // Function to refresh balances - wrap in useCallback to avoid recreating on each render
  const refreshBalances = useCallback(async (walletAddress: string | null | undefined) => {
    if (!walletAddress) {
      console.error('No wallet address provided for refreshBalances');
      return;
    }
    
    console.log(`🔄 Refreshing all balances for wallet: ${walletAddress.slice(0, 8)}...`);
    
    // Refresh SOL balance
    await refreshSolBalance(walletAddress);
    
    // Refresh token balance if token is selected
    if (effectiveToken?.mint) {
      await refreshTokenBalance(walletAddress, effectiveToken.mint);
    }
  }, [effectiveToken?.mint, refreshSolBalance, refreshTokenBalance]);

  // Function to handle amount button selection
  const handleAmountSelect = (selectedAmount: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAmount(selectedAmount);
  };

  // Function to handle selling all tokens
  const handleSellAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Make sure we have a token balance before setting
    if (tokenBalance && parseFloat(tokenBalance) > 0) {
      setAmount(tokenBalance);
    } else {
      // If the token balance is zero but we should have this token, try fetching balances again
      if (effectiveToken) {
        const refreshAndSetAll = async () => {
          if (activeTradingWallet) {
            try {
              setIsLoadingBalances(true);
              
              // Clear any existing error message while we check balances
              setError(null);
              
              // Import walletBalanceService store dynamically to avoid circular dependencies
              const { useWalletBalances } = await import('../../services/walletBalanceService');
              const walletBalanceState = useWalletBalances.getState();
              
              // Check if there's a token balance in the global state
              const tokenData = walletBalanceState.tokenBalances.find(
                tb => tb.mint === effectiveToken.mint
              );
              
              if (tokenData && tokenData.balance && parseFloat(tokenData.balance) > 0) {
                setTokenBalance(tokenData.balance);
                setAmount(tokenData.balance);
                setIsLoadingBalances(false);
                return;
              }
              
              // Try a direct API call as a final attempt
              const balance = await getTokenBalance(activeTradingWallet, effectiveToken.mint);
              if (balance && parseFloat(balance) > 0) {
                setTokenBalance(balance);
                setAmount(balance);
                setIsLoadingBalances(false);
                return;
              }
              
              // One last attempt - try to get all token balances
              const allBalances = await getTokenBalances(activeTradingWallet);
              if (allBalances && allBalances[effectiveToken.mint] && parseFloat(allBalances[effectiveToken.mint]) > 0) {
                console.log(`[TradesBuySell] Found balance in all tokens check: ${allBalances[effectiveToken.mint]}`);
                setTokenBalance(allBalances[effectiveToken.mint]);
                setAmount(allBalances[effectiveToken.mint]);
                setIsLoadingBalances(false);
                return;
              }
              
              // Force a wallet balance update via event and wait a short time
              window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
                detail: {
                  walletPublicKey: activeTradingWallet
                }
              }));
              
              // Wait a bit before giving up
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // One final check after waiting
              const finalBalance = await getTokenBalance(activeTradingWallet, effectiveToken.mint);
              if (finalBalance && parseFloat(finalBalance) > 0) {
                setTokenBalance(finalBalance);
                setAmount(finalBalance);
                return;
              }
              
            } catch (err) {
              console.error('[TradesBuySell] Error fetching token balance for sell all:', err);
            } finally {
              setIsLoadingBalances(false);
            }
          }
          
          // If we reach here, show the error after a short delay
          // This delay gives time for any pending balance updates to arrive
          setTimeout(() => {
            // Check one more time before showing error
            if (parseFloat(tokenBalance) > 0) {
      setAmount(tokenBalance);
    } else {
      setError('No tokens to sell');
            }
          }, 500);
        };
        
        refreshAndSetAll();
      } else {
        // Delay the error to allow other components to update the balance if needed
        setTimeout(() => {
          setError('No tokens to sell');
        }, 500);
      }
    }
  };

  // Toggle between buy and sell modes
  const toggleTradeMode = () => {
    const newMode = tradeMode === 'buy' ? 'sell' : 'buy';
    setTradeMode(newMode);
    
    // Reset amount when switching modes - use 0.1 for buy, 1 for sell
    setAmount(newMode === 'buy' ? '0.1' : '1');
    
    // Clear any previous error or success messages
    setError(null);
    setSuccess(null);
    setTxSignature(null);
    
    // We'll get a new estimate after the state updates
    setEstimatedOutput(null);
  };

  // Call getTradeEstimate when relevant state changes
  useEffect(() => {
    if (effectiveToken?.mint && amount && !isNaN(parseFloat(amount))) {
      // Create a local function inside this effect to prevent dependency loop
      const estimatePrice = async () => {
        if (!effectiveToken?.mint || !amount) return;
        
        try {
          setIsGettingEstimate(true);
          setError(null);
          
          // Parse the amount as a number
          const parsedAmount = parseFloat(amount);
          
          console.log(`Getting quote for ${tradeMode} mode with ${amount} ${tradeMode === 'buy' ? 'SOL' : effectiveToken.symbol}`);
          
          // For Jupiter API, we pass the amount as is - the trade engine will handle the conversion
          // IMPORTANT: do NOT convert the amount ourselves - let tradeEngine.getQuote function expects the parameters
          
          // Buy: SOL -> Token, Sell: Token -> SOL
          const params = tradeMode === 'buy' 
            ? {
                inputMint: SOL_MINT,
                outputMint: effectiveToken.mint,
                amount: amount, // Use original string amount
                slippagePercent: slippage
              }
            : {
                inputMint: effectiveToken.mint,
                outputMint: SOL_MINT,
                amount: amount, // Use original string amount 
                slippagePercent: slippage
              };
          
          // Get quote from trade engine
          const quote = await tradeEngine.getQuote(params);
          
          // Log the FULL quote result for debugging
          console.log('Raw quote result:', JSON.stringify(quote, null, 2));
          
          let outputAmount = quote.estimatedOutputAmount;
          
          // SIMPLIFIED APPROACH:
          // Just log what we receive and use it directly with minimal processing
          console.log(`Raw estimated output: ${outputAmount}, type: ${typeof outputAmount}`);
          
          if (tradeMode === 'buy') {
            // For buy mode (SOL -> Token), use the token conversion from 6 decimals
            if (typeof outputAmount === 'string') {
              // Remove commas to get a clean number
              const rawAmount = outputAmount.replace(/,/g, '');
              const numAmount = parseFloat(rawAmount);
              
              // Convert from smallest units (e.g., 3,299,449,102,802) to standard units using 6 decimals
              const standardAmount = numAmount / 1_000_000;
              
              // Format with commas for thousands and keep decimal precision
              outputAmount = standardAmount.toLocaleString(undefined, { 
                minimumFractionDigits: 6,
                maximumFractionDigits: 6 
              });
              
              console.log(`Converted token amount from ${rawAmount} to ${outputAmount} using 6 decimal precision`);
            }
          } else {
            // For SELL mode: DO NOT convert the value, just format it nicely
            // The API already returns the correct SOL amount
            if (typeof outputAmount === 'string') {
              const rawAmount = outputAmount.replace(/,/g, '');
              const numAmount = parseFloat(rawAmount);
              
              console.log(`SOL value before formatting: ${numAmount}`);
              
              // The key issue: The value is returned in lamports (smallest unit)
              // 1 SOL = 1,000,000 lamports (10^6)
              // Values like 0.000000002 are very likely to be in lamports
              // Extremely small values (< 0.001) are likely lamports that need conversion
              
              let formattedAmount;
              if (numAmount < 0.001) {
                // Convert from lamports to SOL by multiplying by 10^6
                // This fixes values like 0.000000002 to become 0.002
                formattedAmount = numAmount * 1_000_000;
                console.log(`Converting from lamports: ${numAmount} → ${formattedAmount} SOL`);
              } else {
                // Already in SOL format
                formattedAmount = numAmount;
              }
              
              // Format the SOL amount with 9 decimal places
              outputAmount = formattedAmount.toFixed(9);
              
              console.log(`Final formatted SOL amount: ${outputAmount}`);
            }
          }
          
          // Set the properly formatted output
          setEstimatedOutput(outputAmount);
        } catch (error) {
          // Only log and show errors that aren't related to route availability
          // since we know the trade can still execute successfully
          if (error instanceof Error && !error.message.includes('No route available')) {
            console.error('Error getting price estimate:', error);
            setError(`Could not get price estimate: ${error.message}`);
          }
          setEstimatedOutput(null);
        } finally {
          setIsGettingEstimate(false);
        }
      };
      
      estimatePrice();
    } else {
      setEstimatedOutput(null);
    }
  }, [effectiveToken?.mint, effectiveToken?.symbol, amount, tradeMode, slippage]);

  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setSuccessWithTimeout("Copied to clipboard!");
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setErrorWithTimeout('Failed to copy to clipboard');
      });
  };

  // Clear notification timeouts on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [notificationTimeout, errorTimeout]);

  // Function to set error with timeout
  const setErrorWithTimeout = (message: string | React.ReactNode) => {
    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
    }
    setError(message);
    // Set new timeout to clear error after 5 seconds
    const timeout = setTimeout(() => setError(null), 5000);
    setErrorTimeout(timeout);
  };

  // Function to set success with timeout
  const setSuccessWithTimeout = (message: string) => {
    // Clear any existing success timeout
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }
    setSuccess(message);
    // Set new timeout to clear success after 5 seconds
    const timeout = setTimeout(() => setSuccess(null), 5000);
    setNotificationTimeout(timeout);
  };

  // Modify the executeTrade function to use the same method as ActivePositions for selling
  const executeTrade = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any existing notification timeouts
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
      setNotificationTimeout(null);
    }
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }
    
    if (!walletUser.walletAddress) {
      setErrorWithTimeout('You must be logged in to use the Trade feature');
      return;
    }
    
    if (!effectiveToken) {
      setErrorWithTimeout('Please select a token first');
      return;
    }
    
    if (!activeTradingWallet) {
      setErrorWithTimeout('Please activate a trading wallet in your wallet dashboard first');
      return;
    }
    
    // Validate amount
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setErrorWithTimeout('Please enter a valid amount');
      return;
    }
    
    // Check balance for the trade
    if (tradeMode === 'buy') {
      // Check if there's enough SOL 
      if (amountNum > parseFloat(solBalance)) {
        setErrorWithTimeout(`Insufficient SOL balance. You have ${solBalance} SOL.`);
        return;
      }
    } else {
      // Don't allow trades if the balance is pending update
      if (isBalancePending) {
        setErrorWithTimeout('Balance update pending. Please wait a moment and try again.');
        return;
      }
      
      // Check if there's enough token balance
      if (amountNum > parseFloat(tokenBalance)) {
        setErrorWithTimeout(`Insufficient ${effectiveToken.symbol} balance. You have ${tokenBalance} ${effectiveToken.symbol}.`);
        return;
      }
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setTxSignature(null);
      
      let result: any;
      
      if (tradeMode === 'sell') {
        // Use the same method as ActivePositions for selling
        console.log(`Executing sell using jupiterService: ${amount} ${effectiveToken.symbol}`);
        
        // If this is a sell, immediately mark balance as pending update to prevent double-sells
        setIsBalancePending(true);
        
        // Also update UI immediately to prevent visual double-sells
        const optimisticBalance = Math.max(0, parseFloat(tokenBalance) - amountNum).toString();
        setTokenBalance(optimisticBalance);
        
        // Import jupiterService
        const { jupiterService } = await import('../../services/jupiterService');
        
        // Use the same method as ActivePositions
        result = await jupiterService.sellToken({
          tokenMint: effectiveToken.mint,
          amount: amountNum,
          slippage: parseFloat(slippage)
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Sell transaction failed');
        }
        
        // Set transaction signature from the result
        setTxSignature(result.txId);
        
      } else {
        // For buy trades, continue using the existing tradeEngine method
        console.log(`Executing buy trade: ${amount} SOL for ${effectiveToken.symbol}`);
        
        // Configure trade parameters with ORIGINAL string amount (not parsed) to preserve precision
        const tradeParams = {
          inputMint: SOL_MINT,
          outputMint: effectiveToken.mint,
          amount: amount, // Use the original string amount to preserve precision
          userWalletAddress: walletUser.walletAddress,
          tradingWalletAddress: activeTradingWallet,
          slippagePercent: slippage
        };
        
        // Execute the trade using trade engine
        result = await tradeEngine.executeSwap(tradeParams);
        
        // Set transaction signature
        setTxSignature(result.signature);
      }
      
      // Show success message for 10 seconds
      setSuccessWithTimeout(`${tradeMode === 'buy' ? 'Buy' : 'Sell'} transaction executed successfully!`);
      
      // Set a timeout to clear the success message and transaction signature after 10 seconds
      const timeout = setTimeout(() => {
        setSuccess(null);
        setTxSignature(null);
      }, 10000); // 10 seconds
      
      setNotificationTimeout(timeout);
      
      // Immediately force wallet refresh events
      window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
        detail: {
          walletPublicKey: activeTradingWallet
        }
      }));
      
      // Add a function to fetch both SOL and token balances
      const fetchBalances = async (forceRefresh = false) => {
        try {
          if (!activeTradingWallet) return;
          
          // Fetch SOL balance
          const newSolBalance = await getBalance(activeTradingWallet);
          const solBalanceFormatted = (newSolBalance / 1_000_000_000).toFixed(5);
          setSolBalance(solBalanceFormatted);
          
          // Fetch token balance if we have a token
          if (effectiveToken?.mint) {
            const newTokenBalance = await getTokenBalance(activeTradingWallet, effectiveToken.mint);
            setTokenBalance(newTokenBalance);
            
            // If token balance is zero after a sell, reset amount
            if (tradeMode === 'sell' && parseFloat(newTokenBalance) === 0) {
              setAmount('1');
            }
          }
          
          setIsBalancePending(false);
        } catch (err) {
          console.error('[TradesBuySell] Error fetching balances:', err);
        }
      };
      
      // After a successful trade, poll for balance updates
      const pollBalanceUpdates = async (attempts = 30, interval = 200) => {
        console.log(`Starting balance polling for ${attempts} attempts every ${interval}ms`);
        
        // Listen for direct token refresh events during the polling period
        const handleTokenRefresh = (event: Event) => {
          const customEvent = event as CustomEvent<{address: string, tokenMint?: string, timestamp: number}>;
          
          if (customEvent.detail.address === activeTradingWallet) {
            console.log('🔄 Received token refresh event, updating balances immediately');
            // If a specific token refresh matches our token, update immediately
            if (!customEvent.detail.tokenMint || customEvent.detail.tokenMint === effectiveToken?.mint) {
              // Force reload balances right away
              fetchBalances(true);
            }
          }
        };
        
        // Early direct refresh after trade using refreshWalletTokens
        try {
          const { refreshWalletTokens } = await import('../../services/solanatrackerService');
          console.log(`🔍 Initial aggressive refresh attempt for ${effectiveToken?.symbol || effectiveToken?.mint}`);
          
          // First attempt at 0ms (immediate)
          const result = await refreshWalletTokens(activeTradingWallet || '', effectiveToken?.mint, true);
          
          if (result.status === 'success' && result.data?.tokens?.length > 0) {
            const refreshedToken = result.data.tokens[0];
            console.log(`📊 Initial refresh successful: Balance = ${refreshedToken.balance}`);
            
            if (refreshedToken.balance && refreshedToken.balance !== '0') {
              setTokenBalance(refreshedToken.balance);
              setIsBalancePending(false);
              // Don't return early, continue with polling as backup
            }
          }
        } catch (err) {
          console.error('Error during initial refresh:', err);
          // Continue with normal polling
        }
        
        // Add event listener for token refresh
        window.addEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
        
        // Also try to get balance directly from solanatrackerService
        try {
          // Import solanatrackerService dynamically
          const { getWalletTokensWithMetadata } = await import('../../services/solanatrackerService');
          const tokenResponse = await getWalletTokensWithMetadata(activeTradingWallet || '');
          
          if (tokenResponse.status === 'success' && tokenResponse.data?.tokens) {
            const targetToken = tokenResponse.data.tokens.find(
              (t: any) => t.mint === effectiveToken?.mint
            );
            
            if (targetToken && targetToken.balance) {
              console.log(`📊 Found token balance via getWalletTokensWithMetadata: ${targetToken.balance}`);
              setTokenBalance(targetToken.balance);
              setIsBalancePending(false);
            }
          }
        } catch (err) {
          console.error('Error fetching token metadata:', err);
          // Continue with normal polling
        }
        
        let attempt = 0;
        const pollInterval = setInterval(async () => {
          attempt++;
          console.log(`Balance polling attempt ${attempt}/${attempts}`);
          
          try {
            await fetchBalances(true);
            
            // If we've reached the max attempts, stop polling
            if (attempt >= attempts) {
              clearInterval(pollInterval);
              window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
              console.log('Balance polling completed');
            }
          } catch (error) {
            console.error(`Error in polling attempt ${attempt}:`, error);
            
            // If we've reached the max attempts, stop polling even on error
            if (attempt >= attempts) {
              clearInterval(pollInterval);
              window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
              console.log('Balance polling completed with errors');
            }
          }
        }, interval);
        
        // Also set up a final cleanup after maximum time
        setTimeout(() => {
          clearInterval(pollInterval);
          window.removeEventListener('ekko:wallet_tokens_refreshed', handleTokenRefresh);
          console.log('Balance polling cleanup completed');
        }, attempts * interval + 1000);
      };
      
      // Start polling for balance updates
      pollBalanceUpdates();
      
      // Dispatch a global trade completed event that other components can listen for
      window.dispatchEvent(new CustomEvent('ekko:trade_completed', {
        detail: {
          type: tradeMode,
          token: effectiveToken,
          amount: amount,
          walletAddress: activeTradingWallet,
          timestamp: Date.now()
        }
      }));
      
      // Additionally, try to directly refresh the token data from all sources
      try {
        // Import necessary services
        const { refreshWalletTokens } = await import('../../services/solanatrackerService');
        
        console.log(`Directly refreshing token data for ${effectiveToken.mint} after transaction`);
        
        // Force a refresh from the token tracker service - this might be faster than blockchain queries
        const refreshResult = await refreshWalletTokens(activeTradingWallet || '', effectiveToken.mint, true);
        
        if (refreshResult.status === 'success' && refreshResult.data?.tokens?.length > 0) {
          const refreshedToken = refreshResult.data.tokens[0];
          console.log(`✅ Successfully refreshed token data: ${refreshedToken.balance}`);
          setTokenBalance(refreshedToken.balance);
        } else {
          console.log(`⚠️ Token refresh returned no data, continuing with polling...`);
        }
      } catch (refreshError) {
        console.error('Error during manual token refresh:', refreshError);
        // Continue with normal balance polling even if this fails
      }
      
    } catch (error) {
      console.error('Error executing trade:', error);
      
      // Reset balance pending flag
      setIsBalancePending(false);
      
      // If sell failed, restore original balance in UI
      if (tradeMode === 'sell' && effectiveToken?.mint) {
        // Recheck balance to ensure UI is correct
        refreshBalances(activeTradingWallet || '');
      }
      
      // Handle different types of errors
      if (error instanceof Error) {
        // Handle specific error messages 
        if (error.message.includes("Insufficient")) {
          setErrorWithTimeout(`Insufficient balance: ${error.message}`);
        } else if (error.message.includes("slippage")) {
          setErrorWithTimeout(`Slippage error: ${error.message}. Try increasing slippage tolerance.`);
        } else if (error.message.includes("Token not found")) {
          setErrorWithTimeout(`This token is too new or not supported by Jupiter yet.`);
        } else if (error.message.includes("TOKEN_NOT_TRADABLE")) {
          setErrorWithTimeout(`This token cannot be traded on Jupiter at this time. It may be too new, have low liquidity, or be restricted from trading.`);
        } else {
          setErrorWithTimeout(`Failed to execute trade: ${error.message}`);
        }
      } else {
        setErrorWithTimeout('An unknown error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Format number with appropriate precision
  const formatNumber = (num: number | string, maxDecimals: number = 6): string => {
    // If this is already a pre-formatted estimated output string, just return it
    if (estimatedOutput === num && typeof num === 'string') {
      return num;
    }
    
    // Otherwise parse and format
    const value = typeof num === 'string' ? parseFloat(num.replace(/,/g, '')) : num;
    
    if (isNaN(value)) return '0';
    
    // For very small values, use fixed notation with 9 decimals instead of scientific notation
    if (value < 0.000001) return value.toFixed(9);
    if (value < 0.001) return value.toFixed(6);
    if (value < 1) return value.toFixed(Math.min(4, maxDecimals));
    
    if (value < 1000) return value.toFixed(Math.min(2, maxDecimals));
    
    return value.toLocaleString(undefined, { maximumFractionDigits: Math.min(2, maxDecimals) });
  };

  // Define styles for inputs
  const customInputStyles = `
    /* Styling for number input arrows - remove them completely */
    input[type=number]::-webkit-outer-spin-button,
    input[type=number]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    /* For Firefox */
    input[type=number] {
      -moz-appearance: textfield;
    }
    
    /* AGGRESSIVE white background overrides for ALL possible selectors */
    .trades-buy-sell input,
    .trades-buy-sell input[type="text"],
    .trades-buy-sell input[type="number"],
    .trades-buy-sell textarea,
    div[class*="trades-buy-sell"] input,
    div[class*="trades-buy-sell"] input[type="text"],
    div[class*="trades-buy-sell"] input[type="number"],
    [data-widget-type="trades-buy-sell"] input,
    [data-widget-type="trades-buy-sell"] input[type="text"],
    [data-widget-type="trades-buy-sell"] input[type="number"] {
      background-color: white !important;
      background: white !important;
      border: 1px solid #d1d5db !important;
      color: black !important;
    }
    
    /* Even more specific overrides */
    .trades-buy-sell div input,
    .trades-buy-sell .relative input,
    .trades-buy-sell [class*="input"] {
      background-color: white !important;
      background: white !important;
      color: black !important;
    }
    
    /* White input style for TradesBuySell widget */
    .glass-input {
      background-color: white !important;
      background: white !important;
      border: 1px solid rgba(156, 163, 175, 0.5) !important;
      color: black !important;
    }
    
    /* Focus states for all inputs */
    .trades-buy-sell input:focus,
    .trades-buy-sell input[type="text"]:focus,
    .trades-buy-sell input[type="number"]:focus,
    div[class*="trades-buy-sell"] input:focus,
    [data-widget-type="trades-buy-sell"] input:focus,
    .slippage-input:focus, 
    .amount-input:focus, 
    .glass-input:focus {
      border-color: #24c35b !important;
      outline: none;
      box-shadow: 0 0 0 1px rgba(36, 195, 91, 0.2) !important;
      background-color: white !important;
      background: white !important;
    }
  `;

  // Add a balance update event listener when the component initializes
  useEffect(() => {
    // Function to handle token balance updates
    const handleTokenBalanceUpdate = (event: CustomEvent<{address: string, mint: string, balance: string, forceRefresh?: boolean}>) => {
      const { address, mint, balance, forceRefresh } = event.detail;
      
      // Only process if this update is for our active trading wallet and selected token
      if (
        effectiveToken?.mint === mint && 
        activeTradingWallet === address
      ) {
        console.log(`[TradesBuySell] Balance update received for ${mint}: ${balance !== null ? balance : 'refresh requested'}`);
        
        // If this is a forced refresh with null balance, we need to fetch the balance directly
        if (forceRefresh && balance === null) {
          const fetchFreshBalance = async () => {
            try {
              setIsLoadingBalances(true);
              const freshBalance = await getTokenBalance(address, mint);
              console.log(`[TradesBuySell] Fresh balance fetched for ${mint}: ${freshBalance}`);
              setTokenBalance(freshBalance);
              setIsLoadingBalances(false);
            } catch (err) {
              console.error('[TradesBuySell] Error fetching fresh balance:', err);
              setIsLoadingBalances(false);
            }
          };
          
          fetchFreshBalance();
        } 
        // If we already have a balance, use it directly
        else if (balance !== null) {
          setTokenBalance(balance);
        }
      }
    };
    
    // Function to handle SOL balance updates
    const handleSolBalanceUpdate = (event: CustomEvent<{address: string, balance: number | null, forceRefresh?: boolean}>) => {
      const { address, balance, forceRefresh } = event.detail;
      
      // Only process if this update is for our active trading wallet
      if (activeTradingWallet === address) {
        console.log(`[TradesBuySell] SOL balance update received: ${balance !== null ? balance : 'refresh requested'}`);
        
        // If this is a forced refresh with null balance, we need to fetch the balance directly
        if ((forceRefresh && balance === null) || balance === null) {
          const fetchFreshBalance = async () => {
            try {
              setIsLoadingBalances(true);
              const newBalance = await getBalance(address);
              const solBalanceFormatted = (newBalance / 1_000_000_000).toFixed(5); // Convert lamports to SOL
              console.log(`[TradesBuySell] Fresh SOL balance fetched: ${solBalanceFormatted}`);
              setSolBalance(solBalanceFormatted);
              setIsLoadingBalances(false);
            } catch (err) {
              console.error('[TradesBuySell] Error fetching fresh SOL balance:', err);
              setIsLoadingBalances(false);
            }
          };
          
          fetchFreshBalance();
        } 
        // If we already have a balance, use it directly
        else if (balance !== null) {
          const solBalanceFormatted = (balance / 1_000_000_000).toFixed(5); // Convert lamports to SOL
          setSolBalance(solBalanceFormatted);
        }
      }
    };
    
    // Add event listeners
    window.addEventListener('ekko:token_balance_update', handleTokenBalanceUpdate as EventListener);
    window.addEventListener('ekko:sol_balance_update', handleSolBalanceUpdate as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('ekko:token_balance_update', handleTokenBalanceUpdate as EventListener);
      window.removeEventListener('ekko:sol_balance_update', handleSolBalanceUpdate as EventListener);
    };
  }, [effectiveToken?.mint, activeTradingWallet]);

  // Modify the update when token selection changes to add a more aggressive refresh mechanism
  useEffect(() => {
    if (selectedToken) {
      setLocalSelectedToken(selectedToken);
      
      // 🔥 **CRITICAL FIX**: Only refresh token balance if we have an active trading wallet
      // Don't interfere with the active trading wallet detection
      if (activeTradingWallet && selectedToken.mint) {
        console.log(`🔄 Token selected: ${selectedToken.symbol}, refreshing token balance only for wallet: ${activeTradingWallet.slice(0, 8)}...`);
        refreshTokenBalance(activeTradingWallet, selectedToken.mint);
      } else if (selectedToken.mint && !activeTradingWallet) {
        console.log('⚠️ Token selected but no active trading wallet available, setting balance to 0');
        setTokenBalance('0');
      }
    }
  }, [selectedToken, activeTradingWallet, refreshTokenBalance]); // 🔥 **CRITICAL**: Add refreshTokenBalance to dependencies

  // Update the useEffect that loads saved slippage preference to also check Supabase
  useEffect(() => {
    const loadSlippagePreferences = async () => {
      try {
        // First check if we have a user that's logged in
        if (walletUser.walletAddress) {
          // Try to load user profile from Supabase
          const userData = await userService.getUserByWallet(walletUser.walletAddress);
          
          if (userData && userData.user_preferences) {
            // First try to get quickBuySlippage from user preferences (global setting)
            if (userData.user_preferences.quickBuySlippage) {
              console.log('[TradesBuySell] Loading global slippage setting from Supabase:', userData.user_preferences.quickBuySlippage);
              setSlippage(userData.user_preferences.quickBuySlippage);
              return; // We found the preference in Supabase, no need to check localStorage
            }
            
            // Fallback to tradeSlippage if quickBuySlippage isn't available
            if (userData.user_preferences.tradeSlippage) {
              console.log('[TradesBuySell] Loading trade slippage from Supabase:', userData.user_preferences.tradeSlippage);
              setSlippage(userData.user_preferences.tradeSlippage);
              return; // We found the preference in Supabase, no need to check localStorage
            }
          }
        }
        
        // If no Supabase setting or user not logged in, try localStorage
        const savedSlippage = localStorage.getItem(SLIPPAGE_STORAGE_KEY);
        if (savedSlippage) {
          console.log('[TradesBuySell] Loading slippage from localStorage:', savedSlippage);
          setSlippage(savedSlippage);
          return;
        }
        
        // If nothing was found, use the default of 20%
        console.log('[TradesBuySell] Using default slippage of 20%');
        setSlippage('20');
      } catch (error) {
        console.error('[TradesBuySell] Error loading slippage preference:', error);
        // Default to 20% on error
        setSlippage('20');
      }
    };
    
    // Load the preferences
    loadSlippagePreferences();
  }, [walletUser.walletAddress]);

  // Update the saveSlippagePreference function to broadcast changes
  const saveSlippagePreference = (value: string) => {
    // Update slippage state
    setSlippage(value);
    
    // Save to localStorage
    localStorage.setItem(SLIPPAGE_STORAGE_KEY, value);
    
    // Show feedback that slippage has been set
    setSuccessWithTimeout(`Slippage SET to ${value}%`);
    
    // Broadcast slippage change to other components
    window.dispatchEvent(new CustomEvent('ekko:slippage_changed', {
      detail: { slippage: value }
    }));
    console.log(`[TradesBuySell] Broadcasting slippage change: ${value}%`);
    
    // If the user is logged in, also save to their profile
    if (walletUser.walletAddress) {
      const saveToProfile = async () => {
        try {
          // Get current user data
          const userData = await userService.getUserByWallet(walletUser.walletAddress);
          if (userData) {
            // Get existing preferences
            const userPrefs = userData.user_preferences || {};
            
            // Update both slippage preferences to ensure they're always synchronized
            // This makes it a truly global setting
            userPrefs.quickBuySlippage = value; // Used by quick buy system
            userPrefs.tradeSlippage = value;    // Used by trade widget
            
            // Save updated preferences
            await userService.updateUserPreferences(userData.id, userPrefs);
            console.log('[TradesBuySell] Global slippage preference saved to user profile:', value);
          }
        } catch (error) {
          console.error('[TradesBuySell] Error saving slippage preference:', error);
          setErrorWithTimeout('Failed to save slippage preference to profile');
        }
      };
      
      saveToProfile();
    }
  };

  // Add a listener for global slippage changes from other components
  useEffect(() => {
    // Handler for slippage change events
    const handleGlobalSlippageChange = (event: CustomEvent<{slippage: string}>) => {
      const { slippage: newSlippage } = event.detail;
      
      // Check if this update is coming from another component (not this one)
      if (newSlippage !== slippage) {
        console.log(`[TradesBuySell] Received global slippage update: ${newSlippage}%`);
        
        // Update our state with the new slippage
        setSlippage(newSlippage);
        
        // Show success message
        setSuccessWithTimeout(`Slippage updated to ${newSlippage}%`);
      }
    };
    
    // Add event listener
    window.addEventListener('ekko:slippage_changed', handleGlobalSlippageChange as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('ekko:slippage_changed', handleGlobalSlippageChange as EventListener);
    };
  }, [slippage]);

  // Add function to handle custom slippage input
  const handleCustomSlippageSubmit = () => {
    // Parse input and validate
    const inputValue = parseFloat(customSlippageInput);
    if (!isNaN(inputValue) && inputValue > 0 && inputValue <= 100) {
      // Use the common save function
      saveSlippagePreference(inputValue.toString());
      setShowCustomSlippage(false);
      setSlippageMenuOpen(false);
    } else {
      // Handle invalid input
      setErrorWithTimeout('Slippage must be a number between 0.1 and 100');
      // Reset to previous value
      setCustomSlippageInput(slippage);
    }
  };

  const content = (
    <div className={`${widgetClassName} trades-buy-sell w-full h-full flex flex-col overflow-hidden border border-gray-200 rounded-xl bg-white shadow-sm`}>
      <style>{customInputStyles}</style>
      {injectGlobalStyles(customStyles)}
      
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
          <h2 className="text-lg font-bold text-black">Quick Trade</h2>
          {/* Trade mode toggle */}
          <div className="mode-toggle flex ml-4 bg-gray-50 rounded-lg p-1">
            <button
              onClick={() => setTradeMode('buy')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                tradeMode === 'buy' ? 'bg-[#24c35b] text-white shadow-sm' : 'text-gray-600 hover:text-black'
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => setTradeMode('sell')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                tradeMode === 'sell' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-600 hover:text-black'
              }`}
            >
              Sell
            </button>
          </div>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-50"
            title="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      {/* Body content */}
      <div className="flex-1 overflow-hidden bg-white p-4">
        {/* Token selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Token
          </label>
          <button
            ref={searchButtonRef}
            onClick={() => setIsTokenSearchModalOpen(true)}
            className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:border-[#24c35b] focus:border-[#24c35b] transition-colors bg-white"
          >
            {effectiveToken ? (
              <div className="flex items-center">
                <img 
                  src={effectiveToken.image_uri || effectiveToken.image_url || `https://via.placeholder.com/32/24c35b/FFFFFF/?text=${effectiveToken.symbol?.charAt(0) || 'T'}`} 
                  alt={effectiveToken.symbol || 'Token'} 
                  className="w-6 h-6 rounded-full mr-2"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `https://via.placeholder.com/32/24c35b/FFFFFF/?text=${effectiveToken.symbol?.charAt(0) || 'T'}`;
                  }}
                />
                <div>
                  <div className="text-sm font-medium text-black">{effectiveToken.symbol || 'Unknown'}</div>
                  <div className="text-xs text-gray-500">{effectiveToken.name || 'Unknown Token'}</div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500">Select a token to trade</div>
            )}
            <ChevronDown size={16} className="text-gray-400" />
          </button>
        </div>
        
        {/* Amount input */}
        <div className="mb-4">
          <div className="flex justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">
              {tradeMode === 'buy' ? 'SOL Amount' : 'Token Amount'}
              </label>
            <div className="text-sm text-gray-500">
              Balance: {tradeMode === 'buy' ? `${solBalance} SOL` : effectiveToken ? tokenBalance : '0'}
            </div>
          </div>
          <div className="relative">
              <input
                type="text"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setAmount(value);
                  }
                }}
              className="w-full py-3 px-3 rounded-lg border border-gray-200 bg-white focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b] text-black"
              style={{ backgroundColor: 'white', background: 'white', color: 'black' }}
              placeholder="Enter amount"
              inputMode="decimal"
            />
              {tradeMode === 'sell' && effectiveToken && (
                <button
                  onClick={handleSellAll}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[#24c35b] hover:text-[#1eaa4e] font-medium"
                >
                MAX
                </button>
              )}
            </div>
          </div>
          
        {/* Quick amount buttons */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {["0.1", "0.5", "1", "2"].map((amt) => (
            <button 
              key={amt}
              onClick={(e) => handleAmountSelect(amt, e)}
              className={`py-2 text-sm rounded-lg border transition-all ${
                amount === amt 
                  ? 'bg-[#24c35b] text-white border-[#24c35b] shadow-sm' 
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#24c35b] hover:text-black'
              }`}
            >
              {amt} {tradeMode === 'buy' ? 'SOL' : ''}
            </button>
          ))}
          </div>
          
        {/* Slippage settings */}
        <div className="mb-4">
          <div className="flex justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">Slippage Tolerance</label>
                <button
                  onClick={() => setSlippageMenuOpen(!slippageMenuOpen)}
              className="text-sm text-[#24c35b] hover:text-[#1eaa4e] flex items-center font-medium"
                >
              {slippageMenuOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              <span className="ml-1">{slippage}%</span>
                </button>
          </div>
                
                {slippageMenuOpen && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
              <div className="grid grid-cols-3 gap-2 mb-2">
                {["1", "2", "5", "10", "15", "20"].map((pct) => (
                      <button
                    key={pct}
                        onClick={() => {
                      setSlippage(pct);
                          setShowCustomSlippage(false);
                      setCustomSlippageInput('');
                        }}
                    className={`py-2 text-sm rounded-lg border transition-all ${
                      slippage === pct 
                        ? 'bg-[#24c35b] text-white border-[#24c35b] shadow-sm' 
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#24c35b] hover:text-black'
                    }`}
                      >
                    {pct}%
                      </button>
                ))}
                    </div>
              
              {/* Custom slippage input */}
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Slippage</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customSlippageInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setCustomSlippageInput(value);
                      }
                    }}
                    className="flex-1 py-2 px-3 rounded-lg border border-gray-200 bg-white focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b] text-black"
                    style={{ backgroundColor: 'white', background: 'white', color: 'black' }}
                    placeholder="Enter %"
                    inputMode="decimal"
                  />
                  <button
                    onClick={() => {
                      if (customSlippageInput && parseFloat(customSlippageInput) > 0) {
                        setSlippage(customSlippageInput);
                        setShowCustomSlippage(false);
                      }
                    }}
                    disabled={!customSlippageInput || parseFloat(customSlippageInput) <= 0}
                    className="px-4 py-2 bg-[#24c35b] text-white rounded-lg hover:bg-[#1eaa4e] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    Set
                  </button>
                </div>
              </div>
            </div>
                )}
        </div>
            
        {/* Price estimation */}
        {estimatedOutput && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-1">Estimated Output</div>
            <div className="text-lg font-semibold text-black">
              {tradeMode === 'buy' ? `${estimatedOutput} ${effectiveToken?.symbol || 'tokens'}` : `${estimatedOutput} SOL`}
            </div>
            {isGettingEstimate && (
              <div className="text-xs text-gray-500 mt-1 flex items-center">
                <Loader size={12} className="animate-spin mr-1" />
                Updating...
              </div>
            )}
          </div>
        )}
        
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-700 text-sm">{error}</div>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-green-700 text-sm">{success}</div>
            {txSignature && (
              <div className="mt-2">
                <a
                  href={`https://solscan.io/tx/${txSignature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-800 text-xs underline"
                >
                  View Transaction
                </a>
              </div>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={executeTrade}
            disabled={
              isLoading || 
              isGettingEstimate || 
              !effectiveToken || 
              !activeTradingWallet || 
              isBalancePending ||
              !amount || 
              parseFloat(amount) <= 0
            }
            className="flex-1 flex items-center justify-center py-3 px-4 bg-[#24c35b] text-white rounded-lg hover:bg-[#1eaa4e] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {isLoading ? (
              <>
                <Loader size={16} className="animate-spin mr-2" />
                <span>{tradeMode === 'buy' ? 'Buying...' : 'Selling...'}</span>
              </>
            ) : (
              <>
                <Zap size={16} className="mr-2" />
                <span>{tradeMode === 'buy' ? 'Buy Token' : 'Sell Token'}</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => refreshBalances(activeTradingWallet)}
            disabled={isLoadingBalances || !activeTradingWallet}
            className="w-12 flex items-center justify-center py-3 px-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Refresh balances"
          >
            <RefreshCcw size={16} className={isLoadingBalances ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Token search modal */}
      <TokenSearchModal
        isOpen={isTokenSearchModalOpen}
        onClose={() => setIsTokenSearchModalOpen(false)}
        onSelectToken={(token: Token, walletTokenBalance?: string) => {
          console.log(`🎯 TradesBuySell: Token selected via TokenSearchModal - ${token.symbol} (${token.mint})`);
          
          setLocalSelectedToken(token);
          setIsTokenSearchModalOpen(false);
          
          // 🚨 DISPATCH TOKEN SELECTION EVENT 🚨
          console.log(`🚨 TradesBuySell: Dispatching ekko:token_selected event for ${token.symbol}`);
          window.dispatchEvent(new CustomEvent('ekko:token_selected', {
            detail: { token, force_refresh: false }
          }));
          
          // If we were passed a balance from the search modal, use it for immediate UI update
          if (walletTokenBalance && walletTokenBalance !== '0') {
            console.log(`Using wallet token balance from search: ${walletTokenBalance} for ${token.symbol || token.mint}`);
            setTokenBalance(walletTokenBalance);
          }
          
          // 🔥 **CRITICAL FIX**: Only refresh token balance if we have an active trading wallet
          // Don't interfere with the active trading wallet detection or SOL balance
          if (activeTradingWallet && token.mint) {
            console.log(`🔄 Token selected from search: ${token.symbol}, refreshing token balance only for wallet: ${activeTradingWallet.slice(0, 8)}...`);
            refreshTokenBalance(activeTradingWallet, token.mint);
          } else if (token.mint && !activeTradingWallet) {
            console.log('⚠️ Token selected from search but no active trading wallet available');
            setTokenBalance('0');
          }
          
          // Reset amount when token changes
          if (tradeMode === 'sell') {
            setAmount('1');
          }
        }}
        referenceElement={searchButtonRef.current}
      />
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
          {content}
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
          <ArrowDownCircle size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Trade</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-50"
            title="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create the widget
export const createTradesBuySellWidget = (selectedToken?: Token, mode: 'buy' | 'sell' = 'buy') => {
  console.log(`🏭 Creating TradesBuySell widget with token:`, selectedToken);
  const uniqueKey = `trade-${selectedToken?.mint || 'default'}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  
  return {
    type: 'trades-buy-sell',
    title: 'Quick Trade',
    content: <TradesBuySell 
      key={uniqueKey}
      selectedToken={selectedToken} 
      initialMode={mode} 
      isWidget={true} 
    />,
    size: { width: 350, height: 650 },
    contentStyle: { 
      padding: 0,
      overflow: 'auto'  // Changed from 'hidden' to 'auto' to allow scrolling if needed
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '2px solid rgba(36, 195, 91, 0.3)',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3), 0 15px 40px rgba(0, 0, 0, 0.2), 0 8px 25px rgba(36, 195, 91, 0.4), 0 4px 15px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden'
    }
  };
};

export default TradesBuySell; 